import React, { useState } from 'react';

import { Button } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid';

import { useGetTranslationOverviewLocaleCatalogs } from '~/hooks/apis/customer-communication/useTranslationOverview';
import useDebounced from '~/hooks/useDebounced';

import TranslationOverviewDetailDrawer from './TranslationOverviewDetailDrawer';
import {
  MUTATE_BLOCKED_REASON,
  SEARCH_DEBOUNCE_MS,
  TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE,
} from './translationOverviewUtils';
import type { TranslationOverviewDetailRow, TranslationOverviewLocaleRow, TranslationOverviewStatus } from './types';

interface Props {
  locale: TranslationOverviewLocaleRow | null;
  onUpload: (locale: string) => void;
  onClose: () => void;
  canMutate: boolean;
}

/** Drill-in showing every catalog for one locale, plus the upload action that locale gates. */
export default function LocaleCatalogsDrawer({ locale, onUpload, onClose, canMutate }: Props) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE,
  });
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<TranslationOverviewStatus | ''>('');

  const search = useDebounced(searchInput, SEARCH_DEBOUNCE_MS);

  const { data, isFetching, error } = useGetTranslationOverviewLocaleCatalogs(locale?.locale, {
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    search: search.trim() || undefined,
    status: status || undefined,
  });

  const rows: Array<TranslationOverviewDetailRow> = (data?.rows ?? []).map((row) => ({
    id: row.catalogId,
    label: row.catalogName,
    status: row.status,
    missingFields: row.missingFields,
    uploadState: row.uploadState,
  }));

  return (
    <TranslationOverviewDetailDrawer
      open={!!locale}
      title={locale?.locale ?? ''}
      subtitle={locale ? `${locale.totalCatalogs.toLocaleString()} catalogs` : undefined}
      rowHeaderName="Catalog"
      searchPlaceholder="Search catalogs"
      rows={rows}
      rowCount={data?.total ?? 0}
      isLoading={isFetching}
      error={error}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      search={searchInput}
      onSearchChange={(value) => {
        setSearchInput(value);
        setPaginationModel((model) => ({ ...model, page: 0 }));
      }}
      status={status}
      onStatusChange={(value) => {
        setStatus(value);
        setPaginationModel((model) => ({ ...model, page: 0 }));
      }}
      actions={
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!canMutate || !locale?.isUploadable}
          title={
            canMutate
              ? locale?.isUploadable
                ? undefined
                : 'Every catalog must be Ready for this locale before uploading'
              : MUTATE_BLOCKED_REASON
          }
          onClick={() => locale && onUpload(locale.locale)}
        >
          Upload to Braze
        </Button>
      }
      onClose={onClose}
    />
  );
}
